/**
 * Performs a prioritized search on an array of items.
 * Items that start with the search term appear before items that merely contain it.
 * 
 * @param items The array of items to search
 * @param searchTerm The term to search for
 * @param getSearchableText A function that extracts searchable text from an item
 * @returns An array of filtered items with "startsWith" matches first
 */
export function prioritizedSearch<T>(
    items: T[],
    searchTerm: string,
    getSearchableText: (item: T) => string | string[]
): T[] {
    if (!searchTerm.trim()) {
        return items;
    }

    const normalizeText = (text: string) =>
        text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const normalizedSearchTerm = normalizeText(searchTerm);

    const startsWithMatches: T[] = [];
    const containsMatches: T[] = [];

    items.forEach(item => {
        const searchableTexts = Array.isArray(getSearchableText(item))
            ? getSearchableText(item) as string[]
            : [getSearchableText(item) as string];

        const normalizedTexts = searchableTexts.map(text => normalizeText(text || ""));

        if (normalizedTexts.some(text => text.startsWith(normalizedSearchTerm))) {
            startsWithMatches.push(item);
        }
        else if (normalizedTexts.some(text => text.includes(normalizedSearchTerm))) {
            containsMatches.push(item);
        }
    });

    return [...startsWithMatches, ...containsMatches];
}